import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Card from '@pelckmans/elementis/components/card';
import Dialog from '@pelckmans/elementis/components/dialog';
import AddLicenseModal from '../../../../../shared-components/AddLicenseModal';
import { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GroupModuleYearUnwind } from '../../../../../api/my-groups';
import ModalContext from '../../../../../contexts/ModalContext';
import { buildGroupProps } from '../../../../../utils/elementisUtils';

interface Props {
  id: string;
  licensedModules: GroupModuleYearUnwind[];
  onSelectedModules: (modules: GroupModuleYearUnwind[]) => void;
  onRefetch: () => void;
}

interface FormValues {
  selectedModules: GroupModuleYearUnwind[];
}

export default function AddMaterialModal({ id, licensedModules, onSelectedModules, onRefetch }: Props) {
  const { closeModal, showModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const { REACT_APP_P_API_URL } = process.env;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { selectedModules: [] },
  });

  const selectedModules = watch('selectedModules') || [];

  const toggleModule = (module: GroupModuleYearUnwind) => {
    const current = selectedModules;
    if (current.find(m => m.id === module.id)) {
      setValue(
        'selectedModules',
        current.filter(m => m.id !== module.id),
        { shouldValidate: true },
      );
    } else {
      setValue('selectedModules', [...current, module], { shouldValidate: true });
    }
  };

  const onSubmit = (data: FormValues) => {
    onSelectedModules(data.selectedModules);
    closeModal(id);
  };

  const handleActivateLicense = () => {
    showModal(<AddLicenseModal id="add-bao-license" onRefetch={onRefetch} />);
  };

  return (
    <Dialog
      id={id}
      aria-label={t('PAGES.MY_GROUPS.MODALS.ADD_MATERIAL.A11Y_LABEL')}
      modal
      onClose={() => closeModal(id)}
      size="sm"
    >
      <Dialog.Header
        aria-label={t('PAGES.MY_GROUPS.MODALS.ADD_MATERIAL.HEADER.CLOSE.A11Y_LABEL')}
        onClose={() => closeModal(id)}
        title={t('PAGES.MY_GROUPS.MODALS.ADD_MATERIAL.HEADER.TITLE')}
      />
      <Dialog.Body
        alertGroupProps={
          errors.selectedModules &&
          buildGroupProps(
            <Alert prominence="danger">{t('PAGES.MY_GROUPS.MODALS.ADD_MATERIAL.ALERTS.NO_MODULES_SELECTED')}</Alert>,
          )
        }
      >
        {licensedModules.length === 0 ? (
          <Alert
            prominence="warning"
            actions={buildGroupProps(
              <Button prominence="neutral" onClick={handleActivateLicense}>
                {t('ACTIONS.ACTIVATE')}
              </Button>,
            )}
          >
            {t('PAGES.MY_GROUPS.MODALS.ADD_MATERIAL.ALERTS.NO_MATERIAL_AVAILABLE')}
          </Alert>
        ) : (
          <Controller
            name="selectedModules"
            control={control}
            rules={{
              validate: value => value && value.length > 0,
            }}
            render={() => (
              <div className="module-container">
                {licensedModules.map(module => (
                  <Card
                    key={module.id}
                    imagePosition="left"
                    name={module.id}
                    as="checkbox"
                    titleTag="h3"
                    selected={!!selectedModules.find(m => m.id === module.id)}
                    titleText={module.catalogName}
                    onClick={() => {
                      toggleModule(module);
                    }}
                    image={`${REACT_APP_P_API_URL}${module.image}`}
                  />
                ))}
              </div>
            )}
          />
        )}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button prominence="neutral" key="cancel" onClick={() => closeModal(id)}>
              {t('ACTIONS.CANCEL')}
            </Button>
            <Button prominence="success" key="create" onClick={handleSubmit(onSubmit)}>
              {t('ACTIONS.CONFIRM')}
            </Button>
          </>,
        )}
      />
    </Dialog>
  );
}
